import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import ComboFilters from "../../ComboFilters";
import classNames from "classnames/bind";
import {
  getAllMonthsUntilNow,
  getGrowthPercentage,
  getLastSixYears,
  months,
  Trend,
} from "../../Reports";
import { If } from "shared/components/control-statements";
import dayjs from "dayjs";
import { Numbers } from "shared/utils/Numbers";
import { Loading } from "@components";
const css = classNames.bind(styles);

export const IdentificationRegistrationDataChart = ({
  selectedData,
  onFetchData,
  onClose,
  loading,
  startOn2025,
  startOnMonth,
  startOnYear,
}) => {
  const getCurrentMonthYear = () => {
    const today = new Date();

    const date = new Date(today.getFullYear(), today.getMonth());
    const mes = months[date.getMonth()];
    const ano = date.getFullYear();

    return {
      name: `${mes} ${ano}`,
      id: `${date.getMonth() + 1}`,
    };
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = getCurrentMonthYear();

  const [data, setData] = useState({
    has_phone: [],
    has_document: [],
    has_email: [],
  });

  const [selectedFilter, setSelectedFilter] = useState("year");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState({
    name: currentYear,
    id: currentYear,
  });

  const daysInMonth = Array.from(
    {
      length: dayjs(
        `${selectedMonth.year}-${String(selectedMonth.id).padStart(2, "0")}`
      )
        .endOf("month")
        .date(),
    },
    (_, i) => i + 1
  );

  const formatData = () => {
    const today = dayjs();

    const result = {};

    if (selectedYear.id < today.year()) return data;

    const isYearFilter = selectedFilter === "year";
    const isCurrentYear = selectedYear.id === today.year();

    const isMonthFilter = selectedFilter === "month";
    const isCurrentMonth =
      selectedYear?.id === today.year() &&
      Number(selectedMonth?.id) === today.month() + 1;

    let limit = null;

    for (const key in data) {
      result[key] = data[key].map((value) => (value === 0 ? null : value));
    }

    if (isYearFilter && isCurrentYear) limit = today.month();

    if (isMonthFilter)
      if (isCurrentMonth) limit = today.date() - 2;
      else return result;

    for (const key in result) {
      result[key] =
        limit !== null ? result[key].slice(0, limit + 1) : result[key];
    }

    return result;
  };

  const series = formatData();

  const roundValue = (v) => {
    return Math.round(v / 10) * 10;
  };

  const getVisibleSeriesMin = (selected) => {
    let min = Infinity;
    let max = -Infinity;

    const labels = {
      "Tem CPF": "has_document",
      "Tem E-mail": "has_email",
      "Tem telefone": "has_phone",
    };

    for (const label in selected) {
      if (!selected[label]) continue;

      const key = labels[label];
      const serieData = data[key];
      if (!serieData) continue;

      const values = serieData
        .map((d) => Number(d))
        .filter((v) => !isNaN(v) && v !== 0);

      if (values.length === 0) continue;

      const localMin = Math.min(...values);
      const localMax = Math.max(...values);
      min = Math.min(min, localMin);
      max = Math.max(max, localMax);
    }

    if (!isFinite(min)) return 0;

    const padding = (max - min) * 0.2;
    const ajustado = Math.max(0, Math.floor(min - padding));
    const rounded = roundValue(ajustado);

    return rounded;
  };

  const handleLegendSelectChanged = (e) => {
    const instance = echartsRef.current.getEchartsInstance();
    const selected = e.selected;

    const newMin = getVisibleSeriesMin(selected);

    instance.setOption({
      yAxis: {
        startValue: newMin,
      },
    });
  };

  const buildOptions = () => {
    return {
      grid: {
        left: 10,
        right: 30,
        top: 30,
        bottom: 70,
        containLabel: true,
      },
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "none",
        },
        backgroundColor: "#2B2B2B",
        borderRadius: 8,
        padding: [8, 8],
        borderWidth: 0,
        textStyle: {
          color: "#FFF",
          fontSize: 10,
        },
        formatter: (params) => {
          const name = params.name;

          if (params.data === null || params.value == null) return;

          const formattedValue = params.value.toLocaleString("pt-BR");

          return `
            <div>
              <strong>${name}${
            selectedFilter === "month" ? ` de ${selectedMonth.name}` : ""
          }</strong><br/><br/>
              ${params.marker} ${params.seriesName}: <b>${formattedValue}</b>
            </div>
          `;
        },
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        pageIconColor: "#6D6DF2",
        pageIconInactiveColor: "#4C4C4C",
        backgroundColor: "#1C1C1C",
        borderRadius: 10,
        padding: 20,
        pageIconSize: 10,
        pageTextStyle: {
          color: "#B3B3B3",
          fontSize: 10,
          fontWeight: 800,
        },
        pageIcons: {
          horizontal: [
            "path://M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z",
            "path://M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z",
          ],
        },
        top: "bottom",
        icon: "circle",
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        textStyle: {
          color: "#808080",
          fontSize: 12,
        },
        inactiveColor: "#555",
      },
      xAxis: {
        type: "category",
        data: selectedFilter === "year" ? months : Array.from(daysInMonth),
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 1,
            type: "solid",
          },
        },
      },
      yAxis: {
        type: "value",
        axisLine: {
          show: true,
          lineStyle: {
            color: "rgba(217, 217, 217, 0.5)",
          },
        },
        axisLabel: {
          formatter: (value) => `${value.toLocaleString("pt-BR")}`,
        },
        splitLine: {
          lineStyle: {
            color: "rgba(51, 51, 51, 0.55)",
            width: 0,
            type: "solid",
          },
        },
      },
      series: [
        {
          name: "Tem telefone",
          type: "line",
          data: series.has_phone || [],
          color: "#F1B6C6",
          symbolSize: 8,
          itemStyle: {
            color: "#F1B6C6",
            borderColor: "#F1B6C6",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#F1B6C6",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Tem CPF",
          type: "line",
          data: series.has_document || [],
          color: "#55E092",
          symbolSize: 8,
          itemStyle: {
            color: "#55E092",
            borderColor: "#55E092",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#55E092",
          },
          symbol: "circle",
          connectNulls: false,
        },
        {
          name: "Tem E-mail",
          type: "line",
          data: series.has_email || [],
          color: "#FABF55",
          symbolSize: 8,
          itemStyle: {
            color: "#FABF55",
            borderColor: "#FABF55",
            borderWidth: 2,
          },
          lineStyle: {
            width: 2,
            color: "#FABF55",
          },
          symbol: "circle",
          connectNulls: false,
        },
      ],
    };
  };

  const [options, setOptions] = useState(buildOptions());

  const echartsRef = useRef(null);

  useEffect(() => {
    const chart = echartsRef.current?.getEchartsInstance();
    if (chart) {
      setTimeout(() => chart.resize(), 0);
    }
  }, []);

  const fetchData = async () => {
    if (!onFetchData) {
      return;
    }

    setData([]);

    const payload = { year: selectedYear?.id };
    payload.month = selectedFilter === "month" ? selectedMonth?.id : null;

    const results = await onFetchData(payload);

    const series = {
      has_phone: [],
      has_document: [],
      has_email: [],
    };

    results.forEach((result) => {
      for (const key in series) {
        if (
          result &&
          result[key] !== undefined &&
          result[key] !== null &&
          result[key] !== 0
        ) {
          series[key].push(result[key]);
        } else {
          series[key].push(null);
        }
      }
    });

    setData(series);
  };

  useEffect(() => {
    fetchData();
  }, [selectedFilter, selectedYear, selectedMonth]);

  useEffect(() => {
    setOptions(buildOptions());
  }, [data]);

  return (
    <div className={styles.monthYearLegendChartContainer}>
      <div className={styles.monthYearHeader}>
        <p className={styles.title}>{selectedData?.name}</p>
        <div className="d-flex align-center">
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>

      <div className={styles.monthYearLegendContent}>
        <If cond={loading}>
          <div className={styles.loading}>
            <Loading />
          </div>
        </If>

        <If cond={!loading}>
          <div className={styles.legendContainer}>
            <div className={styles.header}>
              Crescimento no {selectedFilter === "year" ? "Ano" : "Mês"}
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#F1B6C6" }} />
                <p className={styles.name}>Tem telefone</p>
              </div>

              <Trend
                growth={getGrowthPercentage({
                  data: data.has_phone,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#55E092" }} />
                <p className={styles.name}>Tem CPF</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.has_document,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
              />
            </div>
            <div className={styles.legend}>
              <div className="d-flex align-center">
                <div className={styles.dot} style={{ background: "#FABF55" }} />
                <p className={styles.name}>Tem E-mail</p>
              </div>
              <Trend
                growth={getGrowthPercentage({
                  data: data.has_email,
                  selectedFilter,
                  selectedMonth,
                  selectedYear,
                })}
              />
            </div>
          </div>

          <div className={styles.chartContainer}>
            <div className={styles.filters}>
              <div className="d-flex align-center gap-10">
                <div
                  className={css({
                    selectMini: true,
                    selected: selectedFilter === "year",
                  })}
                  onClick={() => setSelectedFilter("year")}
                >
                  Ano
                </div>
                <div
                  className={css({
                    selectMini: true,
                    selected: selectedFilter === "month",
                  })}
                  onClick={() => setSelectedFilter("month")}
                >
                  Mês
                </div>
              </div>

              <div className="d-flex align-center">
                <If cond={selectedFilter === "year"}>
                  <ComboFilters
                    options={getLastSixYears(startOn2025)}
                    onSelect={(option) => setSelectedYear(option)}
                    selectedOption={selectedYear}
                  />
                </If>
                <If cond={selectedFilter === "month"}>
                  <ComboFilters
                    options={getAllMonthsUntilNow(
                      true,
                      startOnMonth,
                      startOnYear
                    )}
                    onSelect={(option) => setSelectedMonth(option)}
                    selectedOption={selectedMonth}
                  />
                </If>
              </div>
            </div>
            <div className={styles.monthYearChart}>
              <ReactECharts
                ref={echartsRef}
                option={options}
                style={{ width: "100%", height: "100%" }}
                notMerge={true}
                lazyUpdate={true}
                opts={{ renderer: "canvas" }}
                onEvents={{
                  legendselectchanged: handleLegendSelectChanged,
                  // mouseover: handleClick,
                }}
              />
            </div>
          </div>
        </If>
      </div>
    </div>
  );
};
